import { useCallback } from 'react';
import { ThreadContact } from '../../../services/fetchEmailThread';
import { EmailContent } from '../../../services/fetchEmailContent';
import { fetchEmailContentBatch } from '../../../services/fetchEmailContentBatch';
import { PREVIOUS_EMAILS_BATCH_SIZE } from '../../../constants/email';

// A contact is "newer" than the active one if it arrived after it - e.g. the customer replied
// again before the agent responded. Its content needs fetching too, or it'd never render - it's
// neither the active message nor a (lazily-loaded) previous one. Compares numeric epoch times, not
// the raw ISO strings - the backend doesn't always include milliseconds, so mixed-precision strings
// can sort incorrectly.
export function findNewerContactIds(contacts: ThreadContact[], activeContactId: string): string[] {
  const activeTimestamp = contacts.find(c => c.contactId === activeContactId)?.initiationTimestamp;
  if (!activeTimestamp) return [];

  const activeTime = new Date(activeTimestamp).getTime();
  return contacts
    .filter(c => c.contactId !== activeContactId && new Date(c.initiationTimestamp).getTime() > activeTime)
    .map(c => c.contactId);
}

// Fetches content for every thread contact newer than the active one, bounded in batches so a
// burst of replies can't fire an unbounded number of concurrent requests.
export function useFetchNewerThreadContacts(
  onError?: (message: string) => void
): (contacts: ThreadContact[], activeContactId: string) => Promise<Map<string, EmailContent>> {
  return useCallback(
    (contacts: ThreadContact[], activeContactId: string) => {
      const newerContactIds = findNewerContactIds(contacts, activeContactId);

      return fetchEmailContentBatch(newerContactIds, PREVIOUS_EMAILS_BATCH_SIZE, (contactId, error) => {
        console.error(`Failed to fetch content for newer email ${contactId}:`, error);
        onError?.('Failed to load a new reply in this thread. Please try again.');
      });
    },
    [onError]
  );
}
